/* eslint-disable no-nested-ternary */
/* eslint-disable react/no-array-index-key */
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  FiSearch, FiInfo, FiAlertTriangle, FiTrendingUp, FiTrendingDown,
} from 'react-icons/fi';
import dayjs from 'dayjs';
import { notify } from '../../Shared/toast';
import dummyProducts from '../../../data/InventoryProductData';

function ProductInfo() {
  const { id } = useParams();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [recentProducts, setRecentProducts] = useState([]);

  useEffect(() => {
    const product = dummyProducts.find((p) => p.id === id);
    if (product) {
      setSelectedProduct(product);
    } else {
      setSelectedProduct(null);
      notify.info('Details of this Product is not available in demo version');
    }
  }, [id]);

  // Simulate loading recently viewed products
  useEffect(() => {
    setRecentProducts(dummyProducts.slice(0, 3));
  }, []);

  const handleSearch = () => {
    const product = dummyProducts.find(
      (p) => p.name.toLowerCase().includes(searchTerm.toLowerCase())
        || p.id.toLowerCase().includes(searchTerm.toLowerCase())
        || (p.nameBn && p.nameBn.includes(searchTerm)),
    );
    setSelectedProduct(product || null);
    setSuggestions([]);
  };

  const handleInputChange = (e) => {
    const { value } = e.target;
    setSearchTerm(value);
    if (value.length > 0) {
      const filtered = dummyProducts.filter(
        (p) => p.name.toLowerCase().includes(value.toLowerCase())
          || p.id.toLowerCase().includes(value.toLowerCase())
          || (p.nameBn && p.nameBn.includes(value)),
      );
      setSuggestions(filtered);
    } else {
      setSuggestions([]);
    }
  };

  const handleSuggestionClick = (product) => {
    setSearchTerm(product.name);
    setSelectedProduct(product);
    setSuggestions([]);
  };

  const getStockStatus = (current, min) => {
    if (current <= min * 0.5) return 'critical';
    if (current <= min) return 'low';
    return 'good';
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold text-gray-800 mb-6">Product Information</h1>

      {/* Search Section */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="relative max-w-xl">
          <div className="flex">
            <input
              type="text"
              placeholder="Search by Name, ID or Barcode"
              value={searchTerm}
              onChange={handleInputChange}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-l-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            />
            <button
              type="button"
              onClick={handleSearch}
              className="px-4 py-2 bg-blue-600 text-white rounded-r-lg hover:bg-blue-700 flex items-center"
            >
              <FiSearch className="mr-2" />
              Search
            </button>
          </div>

          {suggestions.length > 0 && (
            <ul className="absolute z-10 bg-white border border-gray-200 w-full shadow-lg rounded-b-md mt-1 max-h-60 overflow-auto">
              {suggestions.map((item) => (
                <li key={item.id}>
                  <button
                    type="button"
                    onClick={() => handleSuggestionClick(item)}
                    className="w-full text-left px-4 py-2 hover:bg-blue-50 flex justify-between items-center"
                  >
                    <span>
                      {item.name}
                      {' '}
                      (
                      {item.id}
                      )
                    </span>
                    <span className="text-sm text-gray-500">{item.category}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Recently Viewed */}
      {!selectedProduct && searchTerm === '' && (
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-700 mb-3">Recently Viewed</h2>
          <p className="text-sm text-gray-500 mb-4">
            Click on a product to view details.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {recentProducts.map((product) => (
              <div
                key={product.id}
                className="bg-white p-4 rounded-lg border hover:shadow-md cursor-pointer transition-shadow"
                onClick={() => setSelectedProduct(product)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    setSelectedProduct(product);
                  }
                }}
              >
                <div className="flex justify-between items-start">
                  <h3 className="font-medium">{product.name}</h3>
                  <span className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-800">{product.id}</span>
                </div>
                <p className="text-sm text-gray-600 mt-1">{product.category}</p>
                <div className="mt-2 flex justify-between items-center">
                  <span className="font-medium">
                    {product.currentStock}
                    {' '}
                    {product.unit}
                  </span>
                  <span className={`text-sm px-2 py-1 rounded ${
                    getStockStatus(product.currentStock, product.minStockLevel) === 'critical'
                      ? 'bg-red-100 text-red-800'
                      : getStockStatus(product.currentStock, product.minStockLevel) === 'low'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-green-100 text-green-800'
                  }`}
                  >
                    {getStockStatus(product.currentStock, product.minStockLevel).toUpperCase()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Product Details */}
      {selectedProduct ? (
        <div className="bg-white rounded-xl border shadow overflow-hidden">
          {/* Header */}
          <div className="bg-gray-50 px-6 py-4 border-b flex justify-between items-center">
            <div>
              <h2 className="text-xl font-semibold text-gray-800">
                {selectedProduct.name}
                {selectedProduct.nameBn && (
                  <span className="text-gray-500 ml-2">
                    (
                    {selectedProduct.nameBn}
                    )
                  </span>
                )}
              </h2>
              <p className="text-sm text-gray-600">{selectedProduct.id}</p>
            </div>
            <div className={`px-3 py-1 rounded-full text-sm font-medium ${
              getStockStatus(selectedProduct.currentStock, selectedProduct.minStockLevel) === 'critical'
                ? 'bg-red-100 text-red-800'
                : getStockStatus(selectedProduct.currentStock, selectedProduct.minStockLevel) === 'low'
                  ? 'bg-yellow-100 text-yellow-800'
                  : 'bg-green-100 text-green-800'
            }`}
            >
              {getStockStatus(
                selectedProduct.currentStock,
                selectedProduct.minStockLevel,
              ).toUpperCase()}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 p-6">
            {/* Basic Info */}
            <div className="md:col-span-2">
              <h3 className="text-lg font-medium text-gray-800 mb-4">Product Details</h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Category</p>
                  <p className="font-medium">{selectedProduct.category}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Storage Location</p>
                  <p className="font-medium">{selectedProduct.storageLocation}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Supplier</p>
                  <p className="font-medium">{selectedProduct.supplier}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Barcode</p>
                  <p className="font-medium">{selectedProduct.barcode}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Last Restock</p>
                  <p className="font-medium">
                    {dayjs(selectedProduct.lastRestock).format('DD MMM YYYY, h:mm A')}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Expiry Date</p>
                  <p className="font-medium">
                    {dayjs(selectedProduct.expiryDate).format('DD MMM YYYY')}
                  </p>
                </div>
              </div>

              <div className="mt-4">
                <p className="text-sm text-gray-500">Description</p>
                <p className="text-gray-800">{selectedProduct.description}</p>
              </div>
            </div>

            {/* Stock Info */}
            <div>
              <h3 className="text-lg font-medium text-gray-800 mb-4">Stock Information</h3>

              <div className="space-y-4">
                <div className="p-4 bg-blue-50 rounded-lg">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm text-gray-600">Current Stock</p>
                      <p className="text-2xl font-bold text-blue-800">
                        {selectedProduct.currentStock}
                        {' '}
                        {selectedProduct.unit}
                      </p>
                    </div>
                    <FiInfo className="text-blue-500 text-xl" />
                  </div>
                </div>

                <div className="p-4 bg-green-50 rounded-lg">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm text-gray-600">Minimum Stock Level</p>
                      <p className="text-xl font-semibold text-green-800">
                        {selectedProduct.minStockLevel}
                        {' '}
                        {selectedProduct.unit}
                      </p>
                    </div>
                    <FiAlertTriangle className="text-green-500 text-xl" />
                  </div>
                </div>

                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm text-gray-600">Price Per Unit</p>
                      <p className="text-xl font-semibold text-gray-800">
                        ৳
                        {selectedProduct.pricePerUnit.toFixed(2)}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="p-4 bg-purple-50 rounded-lg">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm text-gray-600">Total Value</p>
                      <p className="text-xl font-semibold text-purple-800">
                        ৳
                        {(selectedProduct.currentStock * selectedProduct.pricePerUnit).toFixed(2)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Movement History */}
          <div className="px-6 pb-6">
            <h3 className="text-lg font-medium text-gray-800 mb-4">Recent Movements</h3>
            <div className="overflow-hidden border border-gray-200 rounded-lg">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Quantity</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {selectedProduct.movementHistory.map((movement, index) => (
                    <tr key={index}>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {dayjs(movement.date).format('DD MMM YYYY')}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          movement.type === 'in' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}
                        >
                          {movement.type.toUpperCase()}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center">
                          {movement.type === 'in' ? (
                            <FiTrendingUp className="text-green-500 mr-1" />
                          ) : (
                            <FiTrendingDown className="text-red-500 mr-1" />
                          )}
                          {movement.quantity}
                          {' '}
                          {selectedProduct.unit}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {movement.user}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : searchTerm ? (
        <div className="bg-white p-6 rounded-lg border shadow text-center">
          <p className="text-gray-500">No product found with this name or ID.</p>
        </div>
      ) : null}
    </div>
  );
}

export default ProductInfo;
